import { css } from "styled-components/macro";

/* Bullet markers cycle by depth: disc → circle → square → disc… Pure CSS
   can't express "depth mod 3" (a deeper selector always wins the cascade,
   so the classic `ul ul ul` reset trick only survives one cycle), so the
   per-depth rules are generated here instead of hand-written.

   The shapes are drawn with ::before instead of native list markers because
   native ::marker boxes hang off the text baseline at a font-metric-driven
   height that reads visibly below the line's visual middle in some editor
   fonts, and ::marker accepts no positioning properties (only font, color
   and content) so it can't be nudged. */
const BULLET_CYCLE = ["disc", "circle", "square"];
const MAX_BULLET_DEPTH = 12;

const BULLET_SHAPE_CSS = {
  disc: "border-radius: 50%; border: none; background: currentColor;",
  circle:
    "border-radius: 50%; border: 0.06em solid currentColor; background: transparent;",
  square: "border-radius: 0; border: none; background: currentColor;",
};

const bulletDepthRules = (scope, itemTarget, listSelector) =>
  Array.from({ length: MAX_BULLET_DEPTH }, (_, depth) => {
    const lists = Array(depth + 1)
      .fill(listSelector)
      .join(" ");
    return `${scope} ${lists} ${itemTarget}::before { ${
      BULLET_SHAPE_CSS[BULLET_CYCLE[depth % BULLET_CYCLE.length]]
    } }`;
  }).join("\n  ");

/**
 * The editor's CSS-drawn bullet treatment (marker box + depth-cycled shapes)
 * as a plain CSS string so non-editor surfaces (markdown cards) can render
 * bullets identically.
 *
 * `itemTarget` is the per-item selector suffix the ::before marker attaches
 * to: the editor puts it on the list item's first paragraph
 * ("> li > p:first-child"); markdown cards render list item content without
 * a guaranteed paragraph wrapper, so they attach it to the item itself
 * ("> li"). `listSelector` lets class-marked lists (markdownFormatter's
 * `ul.sw-drawn-bullets`) carry the depth cycle without a container scope.
 */
export const drawnBulletMarkerStyles = (
  scope,
  { itemTarget = "> li > p:first-child", listSelector = "ul" } = {},
) => `
  ${scope} ${listSelector} {
    list-style: none;
  }

  ${scope} ${listSelector} ${itemTarget}::before {
    content: "";
    display: inline-block;
    width: 0.34em;
    height: 0.34em;
    box-sizing: border-box;
    /* hang into the gutter: width (0.34em) + gap (0.5em) */
    margin-left: -0.84em;
    margin-right: 0.5em;
    /* bottom edge sits on the baseline (center at 0.17em); raise so the
       center lands at 0.35em above it. Tune this to move all bullets. */
    transform: translateY(-0.18em);
  }

  /* Marker shapes, cycling disc → circle → square by depth. */
  ${bulletDepthRules(scope, itemTarget, listSelector)}
`;

/**
 * Document typography shared by every surface that renders manuscript
 * content with TipTap: the live editor (scope "#editor") and the Document
 * History preview (scoped to its own container). Keeping these in one
 * builder is what makes the History modal show the same paragraph spacing,
 * heading sizes and bullet treatment the user sees in the editor.
 *
 * `scope` is the element that carries the inline font styles, the
 * `indented` class and the --sw-paragraph-spacing custom property; the
 * TipTap `.ProseMirror` root is expected to be a descendant of it.
 */
export const editorTypographyStyles = (scope) => css`
  ${scope} p {
    line-height: inherit;
    margin: 0;
  }

  ${scope} em {
    font-style: italic;
  }

  ${scope} .ProseMirror {
    tab-size: 4;
    -moz-tab-size: 4;
    text-align: left;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  ${scope} .ProseMirror strong {
    font-weight: bold;
  }

  /* Headings follow the user's selected body font — App.scss has a bare
     h1-h3 { font-family: Inter } rule that would otherwise beat inheritance
     from the scope's inline style. Sizes in em so they scale with the Text
     Size setting. Ladder: h1 1.8, h2 1.5, h3 1.2 (h4/h5 body size, bold). */
  ${scope} h1,
  ${scope} h2,
  ${scope} h3,
  ${scope} h4,
  ${scope} h5 {
    font-weight: bold;
    line-height: 1.4em;
    letter-spacing: -0.5px;
    margin: 0.5em 0;
    font-family: inherit;
  }

  ${scope} h1 {
    font-size: 1.8em;
  }

  ${scope} h2 {
    font-size: 1.5em;
  }

  ${scope} h3 {
    font-size: 1.2em;
  }

  /* Headings scale their below-gap with the paragraph-spacing setting, at
     half strength so a heading stays visually attached to its section
     (full-strength gaps read as floating). Never less than the legacy 0.5em.
     The var is in px (computed from body font size) — em here would resolve
     against the heading's own larger font-size and inflate the gap. Space
     above comes from the previous block's margin-bottom via margin collapse. */
  ${scope} .ProseMirror > h1,
  ${scope} .ProseMirror > h2,
  ${scope} .ProseMirror > h3,
  ${scope} .ProseMirror > h4,
  ${scope} .ProseMirror > h5 {
    margin-bottom: max(0.5em, calc(var(--sw-paragraph-spacing, 0px) / 2));
  }

  /* Empty spacer paragraphs (legacy docs) would collapse without min-height
     because margins are zero */
  ${scope} .ProseMirror > p.is-editor-empty:not(:first-child) {
    min-height: 1.3em;
  }

  /* Fix visual "jumping" bug by using ::before pseudo-element instead of
     text-indent. The text-indent property can cause rendering glitches during
     text reflow in contenteditable. Inline padding supplies the indent without
     the atomic inline-block box that traps WebKit's caret at empty paragraphs.

     margin-bottom carries the user's Paragraph Spacing setting: the custom
     property is set on the scope element (applyEditorComputedStyles for the
     editor, inline style for the History preview), in px derived from Text
     Size. Scoped to top-level paragraphs only — paragraphs inside list items
     keep margin: 0 from the p reset above. */
  ${scope} .ProseMirror > p {
    text-indent: 0 !important;
    margin-bottom: var(--sw-paragraph-spacing, 0);
  }

  ${scope}.indented .ProseMirror > p:not(.is-editor-empty)::before {
    content: "";
    display: inline;
    padding-inline-start: 1.5em;
  }

  /* Iframe-era cascade: only UL had the global 1em vertical margins; OL kept
     the reset's margin: 0 (plus the shared margin-left below). */
  ${scope} ol {
    list-style: decimal;
    margin: 0;
  }

  ${scope} ul {
    margin: 1em auto;
  }

  ${scope} ol,
  ${scope} ul {
    margin-left: 1em;
    text-indent: 0;
  }

  ${scope}.indented ol,
  ${scope}.indented ul {
    margin-left: 2.5em;
  }

  ${scope} ul li {
    margin-left: 0;
  }

  ${scope} :is(ul, ol) > li + li {
    margin-top: 0.3em;
  }

  /* Nested lists: the top-level ul's 1em vertical margin is collapsed so
     sub-bullets sit close to their parent bullet. */
  ${scope} :is(ul, ol) :is(ul, ol) {
    margin-top: 0.2em;
    margin-bottom: 0.2em;
  }

  /* Bullets are CSS-drawn (see drawnBulletMarkerStyles above): an
     inline-block ::before on the item's first paragraph, pulled left into
     the list gutter with a net inline size of 0 so text position matches
     native markers. Anchored to the text baseline (inline-block bottom edge
     = baseline), so it tracks the first line across line-height/text-size
     changes; the translateY raises it so its center sits ~0.35em above the
     baseline (≈ half a typical cap height) — native markers sit lower than
     the line's visual middle in some fonts and can't be repositioned. */
  ${drawnBulletMarkerStyles(scope)}

  /* The main document's App.scss globally resets li { list-style: none },
     which the iframe-era editor never saw. Restore marker inheritance so
     numbers come from the ol rule above. */
  ${scope} li {
    list-style: inherit;
  }

  /* Match bullet text indentation: the checkbox hangs in the marker gutter
     instead of adding another column before the checklist's text. */
  ${scope} ul[data-type="taskList"] {
    list-style: none;
    padding-left: 0;
  }

  ${scope} ul[data-type="taskList"] > li {
    display: grid;
    /* Give the native checkbox real layout bounds so Safari repaints it when
       an item moves or is deleted. Keep the text aligned with bullet lists. */
    grid-template-columns: 0.7em minmax(0, 1fr);
    column-gap: 0.32em;
    margin-left: -1.02em;
    align-items: first baseline;
  }

  ${scope} ul[data-type="taskList"] > li > label {
    /* Share the first text baseline with the content column. The checkbox's
       bottom sits on that baseline, placing its center 0.35em above it,
       exactly like the drawn bullet. Keep its center in the same gutter. */
    white-space: nowrap;
    margin: 0;
    user-select: none;
  }

  ${scope} ul[data-type="taskList"] > li > label input[type="checkbox"] {
    width: 0.7em;
    height: 0.7em;
    font: inherit;
    vertical-align: baseline;
    margin: 0;
    cursor: pointer;
  }

  ${scope} ul[data-type="taskList"] > li > div {
    min-width: 0;
  }

`;

/**
 * Editor content styles migrated from public/static/editor-tiptap.html when
 * the editor iframe was removed. Everything is scoped under #editorContainer
 * so app styles and editor styles can't bleed into each other.
 *
 * Theme selectors (body[data-theme-type=...]) work unchanged because the
 * parent <body> already carries those attributes (lib/Theme.js).
 */
export const editorContentStyles = css`
  /* The editor host. position: relative anchors absolutely-positioned
     overlays (highlight menu, quick edit) in editor content coordinates;
     isolation contains the migrated z-index: 9999/10000 rules that the
     iframe's own stacking context used to sandbox. */
  #editorContainer {
    position: relative;
    isolation: isolate;
    /* Old resizeIframe floor: max(content, 75vh - 115px). */
    min-height: calc(75vh - 115px);
    html.ios-viewport & {
      /* Match the section's iOS height floor so its footer and corners stay visible. */
      min-height: calc(var(--app-height, 100vh) * 0.75 - 115px);
    }
    /* Flex column so #editor stretches to the container's full height
       (iframe-era #editor had height: 100% of the grown iframe). Overlays
       (highlight menu, quick edit) are absolutely positioned inside #editor,
       which clips with overflow-y: hidden — the stretch guarantees they have
       at least the floor's worth of room. */
    display: flex;
    flex-direction: column;
  }

  #editorContainer ::selection {
    background: var(--text-highlight);
  }

  #editorContainer ::-moz-selection {
    background: var(--text-highlight);
  }

  #editor {
    position: relative;
    overflow-y: hidden;
    order: 1;
    font-size: 20px;
    font-family: Quattro, Courier New;
    padding: 0;
    z-index: 9999;
    width: 100%;
    outline: none;
    /* Fill the container (flex column) but keep growing with content. */
    flex: 1 0 auto;
  }

  /* Iframe-era reset: suppress UA focus rings on controls portaled into the
     editor (quick edit textarea/buttons, highlight menu). */
  #editorContainer textarea,
  #editorContainer select,
  #editorContainer input,
  #editorContainer button {
    outline: none;
  }

  /* Document typography (paragraph spacing, headings, lists/bullets) shared
     with the Document History preview. */
  ${editorTypographyStyles("#editor")}

  #editor a {
    text-decoration: underline;
    cursor: pointer;
  }

  #editor img {
    width: calc(100% - 80px);
  }

  #editor .inserted,
  #editor inserted {
    color: var(--primary-cta-high-contrast);
  }

  #editor .sudo-agent-edit-highlight {
    color: inherit;
    background: var(--text-highlight) !important;
    border-radius: 2px;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }

  /* Persisted (fake) selection: painted while the editor is blurred so the
     user's highlight stays visible. Native selection paints closer to line
     boxes than a plain inline background, so this uses paint-only spread to
     better match the height and connected edges without changing layout. */
  #editor .sudo-persisted-selection {
    background: var(--text-highlight);
    box-shadow:
      -0.08em 0 0 var(--text-highlight),
      0.08em 0 0 var(--text-highlight),
      0 -0.12em 0 var(--text-highlight),
      0 0.12em 0 var(--text-highlight);
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }

  #editor img.sudo-persisted-selection {
    outline: 2px solid var(--text-highlight);
  }

  /* While the fake highlight covers annotated text (e.g. the new-comment flow
     blurs the editor into the sidebar composer), the annotation tint and
     yellow underline must not show through — in the iframe the browser's own
     selection paint covered them. Annotation styles are injected per-id by
     commentStore (incl. a :hover variant), hence the !important. */
  #editor span[data-annotation-id]:has(.sudo-persisted-selection) {
    background: transparent !important;
    border-bottom-color: transparent !important;
  }

  /* Toolbar / agent multicolor highlights (ThemeAwareHighlight) — light hex in data-color; dark via rules below */
  #editor mark[data-color] {
    color: inherit !important;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }

  #editor mark[data-color] insert,
  #editor mark[data-color] inserted,
  #editor insert mark[data-color],
  #editor inserted mark[data-color] {
    color: inherit !important;
    background-color: transparent !important;
  }

  /* Light themes: paint background from stored palette hex (not just inline style) */
  body[data-theme-type="light"] & #editor mark[data-color="#FFF3BF"] {
    background-color: #fff3bf;
  }

  body[data-theme-type="light"] & #editor mark[data-color="#E2F6DA"] {
    background-color: #e2f6da;
  }

  body[data-theme-type="light"] & #editor mark[data-color="#E3EEFF"] {
    background-color: #e3eeff;
  }

  body[data-theme-type="light"] & #editor mark[data-color="#FFE5D6"] {
    background-color: #ffe5d6;
  }

  body[data-theme-type="light"] & #editor mark[data-color="#DDF5F1"] {
    background-color: #ddf5f1;
  }

  body[data-theme-type="light"] & #editor mark[data-color="#FFE1EC"] {
    background-color: #ffe1ec;
  }

  /* Dark themes: swap stored light-mode palette hex to legible dark backgrounds. */
  body[data-theme-type="dark"] & #editor mark[data-color="#FFF3BF"] {
    background-color: #5a4b1a !important;
  }

  body[data-theme-type="dark"] & #editor mark[data-color="#E2F6DA"] {
    background-color: #1f3d2b !important;
  }

  body[data-theme-type="dark"] & #editor mark[data-color="#E3EEFF"] {
    background-color: #1e2f4d !important;
  }

  body[data-theme-type="dark"] & #editor mark[data-color="#FFE5D6"] {
    background-color: #4a2e24 !important;
  }

  body[data-theme-type="dark"] & #editor mark[data-color="#DDF5F1"] {
    background-color: #1f3a3a !important;
  }

  body[data-theme-type="dark"] & #editor mark[data-color="#FFE1EC"] {
    background-color: #4a2434 !important;
  }

  /* Legacy single-color marks without data-color */
  #editor mark:not([data-color]) {
    background-color: var(--text-highlight);
    color: var(--grey-0);
  }

  #editor p.is-editor-empty:first-child::before {
    color: var(--grey-2);
    content: attr(data-placeholder);
    float: left;
    height: 0;
    width: auto;
  }

  #editorContainer .ProseMirror > * {
    cursor: text;
  }

  /* tab-size / text-align / white-space / word-wrap come from the shared
     typography block above. */
  #editorContainer .ProseMirror {
    color: var(--grey-0);
    outline: none;
    box-sizing: border-box;
    padding: 12px 40px 40px 40px;
    position: relative;
    z-index: 10000;
    overflow-y: auto;
  }

  #editorContainer .ProseMirror insert {
    color: var(--primary-cta-high-contrast);
    text-decoration: none;
  }

  #editorContainer .ProseMirror delete {
    color: red;
    text-decoration: line-through;
  }

  #editorContainer .ProseMirror .spell-error {
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-color: var(--spellcheck-underline-color, #ff5862);
    text-decoration-thickness: 4px;
    text-underline-offset: 3px;
    cursor: pointer;
  }

  #editorContainer .ProseMirror .grammar-error {
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-color: var(--grammar-underline-color, #3b82f6);
    text-decoration-thickness: 4px;
    text-underline-offset: 3px;
    cursor: pointer;
  }

  #editorContainer .ProseMirror .search-result {
    background-color: var(--text-highlight);
    border-radius: 2px;
    outline: 1px solid transparent;
    transition: all 0.2s ease-in-out;
  }

  #editorContainer .ProseMirror .search-result-selected {
    background-color: var(--primary-cta-transparent-30);
    outline: 1px solid var(--primary-cta-high-contrast);
    transition: outline 0.2s ease-in-out;
  }

`;
